/**
 * Table Column Configurations for Library Entity Types
 *
 * Each entity type has custom columns tailored to display relevant data.
 * Inline-edit metadata (editType, editOptions, editRefKey, dataKey, deriveName,
 * deriveDescription) drives the per-cell editing behaviour in ItemTable.
 */

// ─── Helpers ────────────────────────────────────────────────────────────────

export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
  return date.toLocaleDateString();
};

export const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

export const formatSecurityProperties = (properties) => {
  if (!properties || properties.length === 0) return null;
  return properties;
};

export const securityPropertyMap = {
  'Confidentiality': 'C',
  'Integrity': 'I',
  'Availability': 'A',
  'Authenticity': 'Au',
  'Non-repudiation': 'NR',
  'Authorization': 'Az'
};

// ─── Inline-edit option arrays ───────────────────────────────────────────────

export const ASSET_TYPES = [
  'ECU', 'Sensor', 'Actuator', 'Network', 'Backend',
  'Cloud Service', 'Mobile App', 'Data Object', 'External System'
];

export const SECURITY_PROPERTIES = [
  'Confidentiality', 'Integrity', 'Availability',
  'Authenticity', 'Non-repudiation', 'Authorization'
];

export const STRIDE_CATEGORIES = [
  'SPOOFING', 'TAMPERING', 'REPUDIATION',
  'INFORMATION_DISCLOSURE', 'DENIAL_OF_SERVICE', 'ELEVATION_OF_PRIVILEGE'
];

export const IMPACT_RATINGS = ['Severe', 'Major', 'Moderate', 'Negligible'];

export const ATTACK_VECTORS = ['Physical', 'Local', 'Adjacent', 'Network'];

export const FEASIBILITY_RATINGS = ['High', 'Medium', 'Low', 'Very Low'];

export const CAL_LEVELS = ['CAL1', 'CAL2', 'CAL3', 'CAL4'];


// ─── Column definitions ──────────────────────────────────────────────────────

/**
 * Module Table Columns
 */
export const moduleColumns = [
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    width: '200px',
    editType: 'text',
    dataKey: 'name'
  },
  {
    key: 'purpose',
    label: 'Purpose',
    sortable: false,
    width: '300px',
    render: (row) => truncateText(row.entity_data?.purpose, 150),
    editType: 'textarea',
    dataKey: 'entity_data.purpose'
  },
  {
    key: 'assetRefs',
    label: 'Contained Assets',
    sortable: true,
    width: '250px',
    renderType: 'assetRefs',
    editType: 'multiselect',
    editRefKey: 'assets',
    dataKey: 'entity_data.assetRefs'
  },
  {
    key: 'featureRefs',
    label: 'Contained Features',
    sortable: true,
    width: '250px',
    renderType: 'featureRefs',
    editType: 'multiselect',
    editRefKey: 'features',
    dataKey: 'entity_data.featureRefs'
  },
];

/**
 * Feature Table Columns
 */
export const featureColumns = [
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    width: '200px',
    editType: 'text',
    dataKey: 'name'
  },
  {
    key: 'description',
    label: 'Description',
    sortable: false,
    width: '300px',
    render: (row) => truncateText(row.description, 150),
    editType: 'textarea',
    dataKey: 'description'
  },
  {
    key: 'assetRefs',
    label: 'Contained Assets',
    sortable: true,
    width: '250px',
    renderType: 'assetRefs',
    editType: 'multiselect',
    editRefKey: 'assets',
    dataKey: 'entity_data.assetRefs'
  },
  {
    key: 'moduleRefs',
    label: 'Belonged Module',
    sortable: true,
    width: '200px',
    renderType: 'moduleRefs',
    editType: 'multiselect',
    editRefKey: 'modules',
    dataKey: 'entity_data.moduleRefs'
  }
];

/**
 * Asset Table Columns
 */
export const assetColumns = [
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    width: '180px',
    editType: 'text',
    dataKey: 'name'
  },
  {
    key: 'type',
    label: 'Asset Type',
    sortable: true,
    width: '130px',
    render: (row) => row.entity_data?.type || 'N/A',
    editType: 'text',
    dataKey: 'entity_data.type'
  },
  {
    key: 'subtype',
    label: 'Asset Subtype',
    sortable: true,
    width: '150px',
    renderType: 'subtype',
    editType: 'text',
    dataKey: 'entity_data.subtype'
  },
  {
    key: 'securityProperties',
    label: 'Security (CIA)',
    sortable: false,
    width: '150px',
    renderType: 'securityProperties',
    editType: 'multiselect',
    editOptions: SECURITY_PROPERTIES,
    dataKey: 'entity_data.securityProperties'
  },
  {
    key: 'moduleRefs',
    label: 'Belonged Modules',
    sortable: true,
    width: '200px',
    renderType: 'moduleRefs',
    editType: 'multiselect',
    editRefKey: 'modules',
    dataKey: 'entity_data.moduleRefs'
  },
  {
    key: 'featureRefs',
    label: 'Belonged Features',
    sortable: true,
    width: '250px',
    renderType: 'featureRefs',
    editType: 'multiselect',
    editRefKey: 'features',
    dataKey: 'entity_data.featureRefs'
  }
];

/**
 * Threat Table Columns
 */
export const threatColumns = [
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    width: '200px',
    editType: 'text',
    dataKey: 'name'
  },
  {
    key: 'threatId',
    label: 'Threat ID',
    sortable: true,
    width: '110px',
    render: (row) => row.entity_data?.threatId || '—',
    dataKey: 'entity_data.threatId'
  },
  {
    key: 'strideCategory',
    label: 'STRIDE',
    sortable: true,
    width: '140px',
    render: (row) => row.entity_data?.strideCategory?.replace(/_/g, ' ') || '—',
    editType: 'select',
    editOptions: STRIDE_CATEGORIES,
    dataKey: 'entity_data.strideCategory'
  },
  {
    key: 'securityProperties',
    label: 'Security Properties',
    sortable: false,
    width: '150px',
    renderType: 'securityProperties',
    dataKey: 'entity_data.securityProperties'
  },
  {
    key: 'feasibilityRating',
    label: 'Feasibility',
    sortable: true,
    width: '100px',
    renderType: 'feasibilityRating',
    dataKey: 'entity_data.feasibilityRating'
  },
  {
    key: 'ET',
    label: 'ET',
    sortable: true,
    width: '50px',
    render: (row) => {
      const f = row.entity_data?.feasibilityFactors?.find(f => f.category === 'ET');
      return f ? f.optionCode : '—';
    }
  },
  {
    key: 'SE',
    label: 'SE',
    sortable: true,
    width: '50px',
    render: (row) => {
      const f = row.entity_data?.feasibilityFactors?.find(f => f.category === 'SE');
      return f ? f.optionCode : '—';
    }
  },
  {
    key: 'KoIC',
    label: 'KoIC',
    sortable: true,
    width: '55px',
    render: (row) => {
      const f = row.entity_data?.feasibilityFactors?.find(f => f.category === 'KoIC');
      return f ? f.optionCode : '—';
    }
  },
  {
    key: 'WoO',
    label: 'WoO',
    sortable: true,
    width: '55px',
    render: (row) => {
      const f = row.entity_data?.feasibilityFactors?.find(f => f.category === 'WoO');
      return f ? f.optionCode : '—';
    }
  },
  {
    key: 'Eq',
    label: 'Eq',
    sortable: true,
    width: '50px',
    render: (row) => {
      const f = row.entity_data?.feasibilityFactors?.find(f => f.category === 'Eq');
      return f ? f.optionCode : '—';
    }
  },
  {
    key: 'threatScenario',
    label: 'Threat Scenario',
    sortable: false,
    width: '250px',
    render: (row) => truncateText(row.entity_data?.threatScenario, 100),
    editType: 'textarea',
    dataKey: 'entity_data.threatScenario'
  },
  {
    key: 'catalogueSource',
    label: 'Source',
    sortable: true,
    width: '120px',
    render: (row) => row.entity_data?.catalogueSource || '—',
    dataKey: 'entity_data.catalogueSource'
  }
];

/**
 * Damage Scenario Table Columns
 */
export const damageScenarioColumns = [
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    width: '220px',
    editType: 'text',
    dataKey: 'name'
  },
  {
    key: 'finalImpactRating',
    label: 'Impact Rating',
    sortable: true,
    width: '130px',
    renderType: 'impactRating',
    editType: 'select',
    editOptions: IMPACT_RATINGS,
    dataKey: 'entity_data.finalImpactRating'
  },
  {
    key: 'assetRefs',
    label: 'Assets Affected',
    sortable: true,
    width: '250px',
    renderType: 'assetRefs',
    editType: 'multiselect',
    editRefKey: 'assets',
    dataKey: 'entity_data.assetRefs'
  },
  {
    key: 'description',
    label: 'Description',
    sortable: false,
    width: '300px',
    render: (row) => truncateText(row.description, 150),
    editType: 'textarea',
    dataKey: 'description'
  }
];

/**
 * Attack Path Table Columns
 */
export const attackPathColumns = [
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    width: '220px',
    editType: 'text',
    dataKey: 'name'
  },
  {
    key: 'attackVector',
    label: 'Attack Vector',
    sortable: true,
    width: '130px',
    render: (row) => row.entity_data?.attackVector || 'N/A',
    editType: 'select',
    editOptions: ATTACK_VECTORS,
    dataKey: 'entity_data.attackVector'
  },
  {
    key: 'attackFeasibilityRating',
    label: 'Feasibility',
    sortable: true,
    width: '120px',
    renderType: 'feasibilityRating',
    editType: 'select',
    editOptions: FEASIBILITY_RATINGS,
    dataKey: 'entity_data.attackFeasibilityRating'
  },
  {
    key: 'threatRefs',
    label: 'Threats Exploited',
    sortable: true,
    width: '250px',
    renderType: 'threatRefs',
    editType: 'multiselect',
    editRefKey: 'threats',
    dataKey: 'entity_data.threatRefs'
  },
  {
    key: 'threatAgent',
    label: 'Threat Agent',
    sortable: false,
    width: '180px',
    render: (row) => truncateText(row.entity_data?.threatAgent, 50),
    editType: 'text',
    dataKey: 'entity_data.threatAgent'
  }
];

/**
 * Cybersecurity Goal Table Columns
 * goalStatement acts as the primary display field and drives the item name.
 */
export const goalColumns = [
  {
    key: 'goalStatement',
    label: 'Goal Statement',
    sortable: true,
    width: '350px',
    render: (row) => truncateText(row.entity_data?.goalStatement, 200),
    editType: 'textarea',
    dataKey: 'entity_data.goalStatement',
    // Updating goalStatement also regenerates name (first 100 chars) and description
    deriveName: (v) => (v || '').substring(0, 100),
    deriveDescription: (v) => v || ''
  },
  {
    key: 'securityProperty',
    label: 'Security Property',
    sortable: true,
    width: '150px',
    render: (row) => row.entity_data?.securityProperty || 'N/A',
    editType: 'select',
    editOptions: SECURITY_PROPERTIES.slice(0, 4), // C, I, A, Au only (matches GoalForm)
    dataKey: 'entity_data.securityProperty'
  },
  {
    key: 'calLevel',
    label: 'CAL Level',
    sortable: true,
    width: '100px',
    renderType: 'calLevel',
    editType: 'select',
    editOptions: CAL_LEVELS,
    dataKey: 'entity_data.calLevel'
  }
];

/**
 * Cybersecurity Claim Table Columns
 * claimStatement acts as the primary display field and drives the item name.
 */
export const claimColumns = [
  {
    key: 'claimStatement',
    label: 'Claim Statement',
    sortable: true,
    width: '400px',
    render: (row) => truncateText(row.entity_data?.claimStatement, 200),
    editType: 'textarea',
    dataKey: 'entity_data.claimStatement',
    deriveName: (v) => (v || '').substring(0, 100)
  }
];

/**
 * Control Table Columns
 */
export const controlColumns = [
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    width: '200px',
    editType: 'text',
    dataKey: 'name'
  },
  {
    key: 'controlId',
    label: 'Control ID',
    sortable: true,
    width: '110px',
    render: (row) => row.entity_data?.controlId || '—',
    dataKey: 'entity_data.controlId'
  },
  {
    key: 'controlType',
    label: 'Type',
    sortable: true,
    width: '150px',
    render: (row) => row.entity_data?.controlType || '—',
    editType: 'select',
    editOptions: ['Cryptographic', 'Access Control', 'Secure Communication', 'Firewall', 'IDS/IPS', 'Authentication', 'Secure Boot', 'Code Signing', 'Hardening', 'Monitoring', 'Other'],
    dataKey: 'entity_data.controlType'
  },
  {
    key: 'goalRefs',
    label: 'Goals',
    sortable: false,
    width: '150px',
    renderType: 'goalRefs',
    editType: 'multiselect',
    editRefKey: 'goals',
    dataKey: 'entity_data.goalRefs'
  },
  {
    key: 'threatRefs',
    label: 'Threats',
    sortable: false,
    width: '150px',
    renderType: 'threatRefs',
    editType: 'multiselect',
    editRefKey: 'threats',
    dataKey: 'entity_data.threatRefs'
  },
  {
    key: 'technologyRefs',
    label: 'Technologies',
    sortable: false,
    width: '150px',
    render: (row) => {
      const refs = row.entity_data?.technologyRefs || [];
      return refs.length > 0 ? refs.length + ' linked' : '—';
    },
    dataKey: 'entity_data.technologyRefs'
  }
];

/**
 * Technology Table Columns
 */
export const technologyColumns = [
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    width: '200px',
    editType: 'text',
    dataKey: 'name'
  },
  {
    key: 'technologyId',
    label: 'Technology ID',
    sortable: true,
    width: '120px',
    render: (row) => row.entity_data?.technologyId || '—',
    dataKey: 'entity_data.technologyId'
  },
  {
    key: 'description',
    label: 'Description',
    sortable: false,
    width: '300px',
    render: (row) => truncateText(row.description, 100),
    editType: 'textarea',
    dataKey: 'description'
  }
];

/**
 * Export all column configurations mapped by entity type
 */
export const tableColumns = {
  module: moduleColumns,
  feature: featureColumns,
  asset: assetColumns,
  threat: threatColumns,
  damage_scenario: damageScenarioColumns,
  attack_path: attackPathColumns,
  goal: goalColumns,
  claim: claimColumns,
  control: controlColumns,
  technology: technologyColumns
};
